import { motion } from "framer-motion";
import { Camera, RefreshCcw, Upload } from "lucide-react";

import { useRef, useState } from "react";
import type { ChangeEvent } from "react";

import { getSmileSimulationCopy } from "../model/i18n";
import type {
  SimulationStatus,
  SmileSimulationLanguage,
  SmileSimulationStep,
} from "../model/types";

type CaptureControls = {
  isCaptureDisabled: boolean;
  isSwitchCameraDisabled?: boolean;
  onSwitchCamera?: () => void;
  onCapture: () => void;
  onRetake: () => void;
  onRetrySimulation: () => void;
  onStartSimulation?: () => void;
  onUploadImage: (file: File) => Promise<void>;
  step: SmileSimulationStep;
  simulationStatus: SimulationStatus;
};

type CaptureActionsProps = {
  controls: CaptureControls;
  language: SmileSimulationLanguage;
};

export function CaptureActions({ controls, language }: CaptureActionsProps) {
  const {
    isCaptureDisabled,
    isSwitchCameraDisabled = true,
    onCapture,
    onRetake,
    onRetrySimulation,
    onStartSimulation,
    onSwitchCamera,
    onUploadImage,
    step,
    simulationStatus,
  } = controls;
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const copy = getSmileSimulationCopy(language);

  const handleUploadChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0];
    event.target.value = "";

    if (!nextFile) {
      return;
    }

    setIsUploading(true);

    try {
      await onUploadImage(nextFile);
    } finally {
      setIsUploading(false);
    }
  };

  if (step === "capture") {
    return (
      <div className="flex w-full shrink-0 items-center justify-center gap-4">
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleUploadChange}
        />
        <motion.button
          type="button"
          onClick={() => uploadInputRef.current?.click()}
          disabled={isUploading}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#8F14E5]/20 bg-white/90 text-[#8F14E5] shadow-[0_12px_30px_rgba(143,20,229,0.12)] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-45 sm:h-14 sm:w-14"
          whileTap={isUploading ? undefined : { scale: 0.98 }}
          whileHover={isUploading ? undefined : { scale: 1.02 }}
          aria-label={
            isUploading ? copy.capture.uploadBusy : copy.capture.upload
          }
        >
          <Upload size={20} strokeWidth={2.2} />
        </motion.button>
        <motion.button
          type="button"
          onClick={onCapture}
          disabled={isCaptureDisabled || isUploading}
          className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-white/80 bg-white/90 text-[#8F14E5] shadow-[0_18px_40px_rgba(143,20,229,0.24)] transition disabled:cursor-not-allowed disabled:opacity-45 sm:h-[4.5rem] sm:w-[4.5rem]"
          whileTap={
            isCaptureDisabled || isUploading ? undefined : { scale: 0.94 }
          }
          whileHover={
            isCaptureDisabled || isUploading ? undefined : { scale: 1.04 }
          }
          aria-label="Capture photo"
        >
          <Camera size={30} strokeWidth={2.2} />
        </motion.button>
        <motion.button
          type="button"
          onClick={onSwitchCamera}
          disabled={isSwitchCameraDisabled || isUploading}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#8F14E5]/20 bg-white/90 text-[#8F14E5] shadow-[0_12px_30px_rgba(143,20,229,0.12)] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-45 sm:h-14 sm:w-14"
          whileTap={
            isSwitchCameraDisabled || isUploading ? undefined : { scale: 0.96 }
          }
          whileHover={
            isSwitchCameraDisabled || isUploading ? undefined : { scale: 1.03 }
          }
          aria-label="Switch camera"
        >
          <RefreshCcw size={20} strokeWidth={2.2} />
        </motion.button>
      </div>
    );
  }

  return (
    <div className="flex w-full shrink-0 items-center justify-center gap-3">
      <motion.button
        type="button"
        onClick={onRetake}
        className="rounded-full border border-[#8F14E5]/20 bg-white/90 px-5 py-3 text-sm font-semibold text-[#8F14E5] shadow-[0_12px_30px_rgba(143,20,229,0.12)] transition hover:bg-white"
        whileTap={{ scale: 0.98 }}
        whileHover={{ scale: 1.02 }}
      >
        {copy.capture.retake}
      </motion.button>
      {simulationStatus === "error" && (
        <motion.button
          type="button"
          onClick={onRetrySimulation}
          className="rounded-full bg-[#8F14E5] px-5 py-3 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(143,20,229,0.22)] transition hover:bg-[#7d10ca]"
          whileTap={{ scale: 0.98 }}
          whileHover={{ scale: 1.02 }}
        >
          {copy.capture.retry}
        </motion.button>
      )}
      {simulationStatus === "idle" && onStartSimulation ? (
        <motion.button
          type="button"
          onClick={onStartSimulation}
          className="rounded-full bg-[#8F14E5] px-5 py-3 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(143,20,229,0.22)] transition hover:bg-[#7d10ca]"
          whileTap={{ scale: 0.98 }}
          whileHover={{ scale: 1.02 }}
        >
          {copy.capture.startSimulation}
        </motion.button>
      ) : null}
    </div>
  );
}
